import { useAuth0 } from '@auth0/auth0-react';
import isNil from 'lodash/isNil';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { sessionDataSchema } from '@/components/session/sessionDataSchema';
import { faro } from '@/faro';
import { useStatusQuoSelectionStorage } from '@/sections/StatusQuoAnalysis/useStatusQuoSelectionStorage';
import { shouldLogoutOnOpen } from '@/utils/autoLogout';
import { HASURA_CLAIM_NAMESPACE } from '@/utils/constants';
import { captureClientException } from '@/utils/posthogErrorTracking';
import { sessionStore } from './session.store';

type AccessTokenType = {
  accessToken: string | null;
  logout: () => void;
  isLoadingAccessToken: boolean;
};

export const AccessTokenContext = createContext<AccessTokenType | null>(null);

// Public shared pages (/analysis/<sha256-hash>) render without auth — for
// example when the backend Puppeteer worker navigates there to print a PDF.
// Hydrate the session store synchronously at module load so the first render
// doesn't throw from useSession() while Auth0 is still loading. Match only
// 64-char lowercase hex (sha256 hashes from createSnapshot) to avoid matching
// authenticated routes like /analysis/buildings or /portfolio.
const PUBLIC_PATH_PATTERN = /^\/analysis\/[a-f0-9]{64}(?:[/?#]|$)/;
if (typeof window !== 'undefined' && PUBLIC_PATH_PATTERN.test(window.location.pathname)) {
  // Initial bootstrap with hardcoded defaults so Router can render before the
  // snapshot resolves. SharedStatusQuoBuilding re-dispatches with snapshot
  // session data (language, org type/sector, feature flags) once the GraphQL
  // query lands. See SharedStatusQuoBuilding.tsx.
  sessionStore.send({ type: 'hydrateAnonymous', snapshot: undefined });
}

export const AccessTokenProvider = ({ children }: PropsWithChildren) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const {
    getAccessTokenSilently,
    getIdTokenClaims,
    logout: auth0Logout,
    isLoading: isLoadingAuth0,
    isAuthenticated,
    error,
  } = useAuth0();

  const { clearStoredSelection } = useStatusQuoSelectionStorage();

  const logout = useCallback(() => {
    clearStoredSelection();

    // Clear all cookies that might be storing Auth0 state
    document.cookie.split(';').forEach((cookie) => {
      const [name] = cookie.trim().split('=');
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    sessionStore.send({ type: 'setUnauthenticated' });

    // Use Auth0's logout with full cleanup
    auth0Logout({
      clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
      logoutParams: { returnTo: window.location.origin },
    });
  }, [auth0Logout, clearStoredSelection]);

  useEffect(() => {
    if (!isNil(error)) {
      captureClientException(error, { source: 'auth0_provider_error' });
    }
  }, [error]);

  useEffect(() => {
    const getAccessTokenAndClaims = async () => {
      try {
        if (!isNil(window.Cypress) || !isAuthenticated) {
          faro?.api?.resetUser();
          return;
        }

        const accessToken = await getAccessTokenSilently();
        const tokenClaims = await getIdTokenClaims();
        const hasuraClaims = tokenClaims?.[HASURA_CLAIM_NAMESPACE];

        if (isNil(hasuraClaims)) {
          throw new Error('Hasura claims are undefined');
        }

        setAccessToken(accessToken);

        const sessionDataClaim = hasuraClaims['x-hasura-session-data'];

        const sessionData = JSON.parse(sessionDataClaim);
        const casted = sessionDataSchema.validateSync(sessionData, {
          abortEarly: false,
        });

        sessionStore.send({
          type: 'hydrate',
          data: casted,
        });

        sessionStorage.setItem('org-sector', casted.org.orgSector);

        const orgHasTimeout = !isNil(casted.org.userLogoutIdleTimeout) && casted.org.userLogoutIdleTimeout > 0;

        if (shouldLogoutOnOpen(orgHasTimeout)) {
          logout();
          return;
        }
      } catch (e: any) {
        captureClientException(e, { source: 'access_token_retrieval_failed' });
        logout();
      }
    };

    void getAccessTokenAndClaims();
  }, [getAccessTokenSilently, getIdTokenClaims, isAuthenticated, logout]);

  // E2E testing flow
  useEffect(() => {
    if (window.Cypress) {
      const cypressTokensJson = (window as any).sessionStorage?.getItem('__cypress_auth_tokens__');
      const cypressTokens = cypressTokensJson ? JSON.parse(cypressTokensJson) : null;

      if (!isNil(cypressTokens)) {
        const { access_token } = cypressTokens;
        setAccessToken(access_token);
      }
    }
  }, []);

  return (
    <AccessTokenContext.Provider
      value={{
        accessToken,
        logout,
        isLoadingAccessToken: (isAuthenticated && isNil(accessToken)) || isLoadingAuth0,
      }}
    >
      {children}
    </AccessTokenContext.Provider>
  );
};

export const useAccessToken = () => {
  const context = useContext(AccessTokenContext);
  if (!context) throw new Error('useAccessToken must be used within a AccessTokenContext');
  return context;
};
